// THE OWNER'S OWN RECENT PURCHASE, TOLD BEFORE THE CREDIT — the app's `PurchaseNoticeSheet` (AppealAce/Features/Flow/
// PurchaseNoticeSheet.swift), laid in `/checkout` where the owner pressed, as the compass and credit-confirm sheets are
// (LAUNCH-PF, R244; R182).
//
// IT RENDERS; IT DOES NOT JUDGE. The figures, the date, the state and its can-raise tier are the server's (op
// `purchaseNotice`, the app's one writer). Every sentence is the app's, in the app's order:
//   - the details card's title, contradiction sentence and can-raise sentence (RecentPurchaseCopy.swift — the website's
//     details step renders the same three from `detailsWording.ts`, and this reuses them rather than a second copy);
//   - Tyler's approved A.1, that an appeal on other grounds may still be worth it (`PURCHASE_NOTICE_COPY`);
//   - "What you can still do" and the card's three routes: "That sale isn't right" by its title only (its detail says
//     "correct it below", which is the details step's form), "That wasn't an open-market sale" with its detail, and "My
//     home has a condition or record problem" with A.2 in place of the card's detail (whose "the next step is where you
//     tell us" is untrue here: the next step is the credit sheet);
//   - "Go back", the app's filled button.
//
// THE SAFE PATH IS THE PRIMARY ONE, as at the compass sheet: "Go back" is the filled button and spends nothing. Each route
// goes on to the credit sheet, which still asks before anything is spent.

import type { RefObject } from "react";
import type { PurchaseNotice } from "@contract";
import { HeldLine } from "../components/copy";
import { heldText } from "../components/heldCopy";
import { Actions } from "../components/layout";
import type { PurchaseNoticeChoice } from "../app/purchaseNotice";
import { Words } from "./steps/detailsFields";
import { canRaiseSentenceId, purchaseSentence } from "./steps/detailsRules";
import { words } from "./steps/detailsWording";

export function PurchaseNoticeSheet({
  notice,
  onChoice,
  headingRef,
}: {
  notice: PurchaseNotice;
  onChoice: (choice: PurchaseNoticeChoice) => void;
  headingRef: RefObject<HTMLHeadingElement | null>;
}) {
  return (
    <section
      className="sheet purchase-notice"
      aria-labelledby="purchase-notice-title"
      data-sheet="purchase"
      data-purchase={notice.purchase.amount}
      data-implied-market={notice.impliedMarket.amount}
    >
      <div className="purchase-head">
        <span className="advisory-glyph warn" aria-hidden="true" />
        <div>
          <h2 id="purchase-notice-title" ref={headingRef} tabIndex={-1} data-held-copy="details.purchase.title">
            {words("details.purchase.title")}
          </h2>
          <p className="copy muted" data-held-copy="details.purchase.paid">
            {purchaseSentence(notice, notice.saleDate)}
          </p>
          <Words id={canRaiseSentenceId(notice.risk)} as="p" className="copy muted" />
        </div>
      </div>

      {/* Approved 2026-10-05 (draft A.1): an appeal on other grounds may still be worth it. */}
      <HeldLine id="purchaseNotice.otherGrounds" className="other-grounds" />

      <div className="purchase-card routes-card">
        <Words id="details.grounds.heading" as="h3" />
        <div className="routes-stack">
          <button type="button" className="route" data-route-choice="wrong-sale" onClick={() => onChoice("saleIsntRight")}>
            <Words id="details.purchase.wrongSale.title" as="b" />
          </button>
          <button type="button" className="route" data-route-choice="not-market" onClick={() => onChoice("notOpenMarket")}>
            <Words id="details.purchase.notMarket.title" as="b" />
            <Words id="details.purchase.notMarket.detail" as="span" />
          </button>
          <button type="button" className="route" data-route-choice="grounds" onClick={() => onChoice("otherGrounds")}>
            <Words id="details.grounds.title" as="b" />
            <span data-held-copy="purchaseNotice.groundsDetail">{heldText("purchaseNotice.groundsDetail")}</span>
          </button>
        </div>
      </div>

      <Actions>
        <button className="btn primary block" type="button" data-held-copy="purchaseNotice.goBack" onClick={() => onChoice("goBack")}>
          {heldText("purchaseNotice.goBack")}
        </button>
      </Actions>
    </section>
  );
}
